import type { ReactNode } from "react";
import { Loader2, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Pill, type PillTone } from "@/components/brand/Pill";
import { formatDateTime, formatRelative } from "@/lib/datetime";

interface ConnectionStatusCardProps {
  /** Identifica o card nos testes (`target-card-<id>`). */
  id: string;
  icon: ReactNode;
  overline: string;
  title: string;
  status: { tone: PillTone; label: string; accent: string };
  /** Dados não secretos da conexão. */
  rows: { k: string; v: string; mono?: boolean }[];
  /** Data do último teste bem-sucedido. Ausente (undefined) = a conexão não guarda essa data e o
   * rodapé não mostra a linha; null = guarda, mas nunca houve teste ("Never tested"). */
  lastTested?: string | null;
  onEdit: () => void;
  /** Texto do botão de edição; sem ele, o lápis com o rótulo Edit. A conexão ainda não
   * cadastrada usa o texto, que diz o que o clique faz. */
  editLabel?: string;
  /** Ausente: a conexão não tem teste (hoje, a Configuration) e o card só mostra o Edit. */
  test?: { onClick: () => void; disabled: boolean; pending: boolean; hint?: string };
}

/** Card de uma conexão do sistema alvo que já existe — mesma estrutura visual do painel da
 * carga do ex-kit (SourceLoadPanel): barra de acento, ícone, pílula de status, grade de
 * dados e rodapé. O CBC, que é de cada usuário, ganha card mesmo sem cadastro, com o botão de
 * cadastrar (`editLabel`). Sem botão de excluir: nesta entrega não há exclusão de conexão do alvo. */
export function ConnectionStatusCard({
  id,
  icon,
  overline,
  title,
  status,
  rows,
  lastTested,
  onEdit,
  editLabel,
  test,
}: ConnectionStatusCardProps) {
  const { t } = useTranslation();
  return (
    <article
      data-testid={`target-card-${id}`}
      className="relative bg-white border border-[#e8ebf0] rounded-xl p-5 flex flex-col gap-3.5 overflow-hidden"
    >
      <span
        className="absolute left-0 top-5 w-8 h-[3px] rounded-r-sm"
        style={{ background: status.accent }}
      />
      <header className="flex justify-between items-start gap-3.5">
        <div className="flex gap-3.5 items-start min-w-0">
          <div className="w-11 h-11 rounded-[10px] flex items-center justify-center text-white shrink-0 bg-[linear-gradient(135deg,#0073e6,#25a9e0)]">
            {icon}
          </div>
          <div className="min-w-0">
            <h3 className="font-brand font-bold text-base text-[#08254c] m-0 mb-0.5 tracking-[-0.01em]">
              {title}
            </h3>
            <div className="font-mono text-[10px] text-[#7b8799] tracking-[0.08em] uppercase">
              {overline}
            </div>
          </div>
        </div>
        <Pill tone={status.tone}>{status.label}</Pill>
      </header>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3.5 border-y border-[#f2f4f7]">
        {rows.map((r) => (
          <div key={r.k} className="min-w-0">
            <div className="font-mono text-[9px] text-[#7b8799] tracking-[0.12em] uppercase mb-0.5">
              {r.k}
            </div>
            <div
              title={r.v}
              className={
                "text-xs text-[#08254c] font-medium truncate" + (r.mono ? " font-mono" : "")
              }
            >
              {r.v}
            </div>
          </div>
        ))}
      </div>
      <footer className="flex justify-between items-center gap-2 flex-wrap mt-auto">
        {lastTested !== undefined ? (
          <div
            className="font-mono text-[10px] text-[#7b8799] tracking-[0.04em] flex items-center gap-1.5"
            title={lastTested ? formatDateTime(lastTested) : undefined}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: status.accent }} />
            {lastTested
              ? `${t("Last tested")} ${formatRelative(lastTested)}`
              : t("Never tested")}
          </div>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {test && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={test.onClick}
              disabled={test.disabled || test.pending}
              title={test.disabled ? test.hint : undefined}
            >
              {test.pending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              {t("Test connection")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={onEdit}
            aria-label={editLabel ?? t("Edit")}
            title={editLabel ?? t("Edit")}
          >
            {editLabel ?? <Pencil className="h-3 w-3" />}
          </Button>
        </div>
      </footer>
    </article>
  );
}
